/**
 * Mock Image CSS3D Renderer
 *
 * Web component for rendering artwork with CSS 3D perspective transformation onto a mock frame.
 * Uses normalized corner data (0-1 range) to calculate matrix3d transforms dynamically based on container size.
 */

class MockImageCss3d extends HTMLElement {
  static get observedAttributes() {
    return ['data-corners', 'data-frame-aspect', 'data-artwork-aspect'];
  }

  constructor() {
    super();
    this.resizeObserver = null;
    this.isUpdating = false;
  }

  connectedCallback() {
    try {
      this._readDataAttributes();
      if (!this.corners) {
        console.error('[MockImageCss3d] No valid corners data provided');
        return;
      }

      // Check for uploaded photo artwork override
      this.checkForArtworkOverride();

      // Create lighting and reflection overlay layers
      this.createOverlayLayers();

      // Apply transform immediately on next frame
      requestAnimationFrame(() => this.updateTransform());

      // Get container reference (needed for observer and event listener)
      const container = this.closest('.mock-image-container') || this.parentElement;

      // Always observe container size — handles initial layout (when rAF fires
      // before the element has dimensions) and responsive resizing.
      // For fixed-size contexts like thumbnails this fires once and is idle.
      let resizeTimeout;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isUpdating) return;
        if (resizeTimeout) cancelAnimationFrame(resizeTimeout);
        resizeTimeout = requestAnimationFrame(() => this.updateTransform());
      });
      this.resizeObserver.observe(container);

      // Listen for artwork changes (when user uploads a photo)
      this.artworkChangeHandler = () => this.handleArtworkChange();
      container.addEventListener('artwork-changed', this.artworkChangeHandler);
    } catch (error) {
      console.error('[MockImageCss3d] Error initializing:', error);
    }
  }

  attributeChangedCallback() {
    // Re-read data and recompute transform when attributes change (e.g. after DOM morph)
    if (!this.isConnected) return;
    this._readDataAttributes();
    requestAnimationFrame(() => this.updateTransform());
  }

  _readDataAttributes() {
    try {
      const cornersData = this.dataset.corners;
      if (cornersData) {
        const parsed = JSON.parse(cornersData);
        if (Array.isArray(parsed) && parsed.length === 4) this.corners = parsed;
      }
    } catch { /* keep existing corners on parse error */ }
    this.frameAspect = parseFloat(this.dataset.frameAspect) || this.frameAspect || 1;
    this.artworkAspect = parseFloat(this.dataset.artworkAspect) || this.artworkAspect || 1;
  }

  createOverlayLayers() {
    // Create frame lighting overlay layer
    // This uses the frame image clipped to the artwork area to apply room lighting
    if (this.dataset.frameSrc) {
      // Check if lighting layer already exists to prevent duplicates
      const existing = this.querySelector('.mock-image__frame-lighting');
      if (existing) {
        return; // Already created, skip
      }

      const frameLightingImg = document.createElement('img');
      frameLightingImg.src = this.dataset.frameSrc;
      frameLightingImg.classList.add('mock-image__frame-lighting');
      frameLightingImg.style.opacity = this.dataset.frameLightingOpacity || '0.5';
      frameLightingImg.dataset.blendMode = this.dataset.frameLightingBlend || 'multiply';
      frameLightingImg.alt = '';
      frameLightingImg.setAttribute('aria-hidden', 'true');
      this.appendChild(frameLightingImg);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    const container = this.closest('.mock-image-container') || this.parentElement;
    if (container && this.artworkChangeHandler) {
      container.removeEventListener('artwork-changed', this.artworkChangeHandler);
    }
  }

  /**
   * Check if container has data-artwork-src override (for uploaded photos)
   */
  checkForArtworkOverride() {
    const container = this.closest('.mock-image-container');
    if (!container) return;

    const artworkSrc = container.dataset.artworkSrc;
    if (artworkSrc) {
      const artworkImg = this.querySelector('.mock-image__artwork');
      if (artworkImg) {
        artworkImg.src = artworkSrc;
        artworkImg.removeAttribute('srcset');
        artworkImg.removeAttribute('sizes');
      }
    }
  }

  /**
   * Handle artwork change event (when user uploads a photo)
   */
  handleArtworkChange() {
    this.checkForArtworkOverride();

    // Re-apply transform in case aspect ratio changed
    requestAnimationFrame(() => this.updateTransform());
  }

  updateTransform() {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const container = this.closest('.mock-image-container') || this.parentElement;
      const frameWidth = container.getBoundingClientRect().width;
      const frameHeight = frameWidth / this.frameAspect;

      if (frameWidth === 0 || frameHeight === 0) {
        this.isUpdating = false;
        return;
      }

      // Convert normalized corners (0-1) to pixel coordinates
      const pixelCorners = this.corners.map(c => ({
        x: parseFloat(c.x) * frameWidth,
        y: parseFloat(c.y) * frameHeight
      }));

      // Calculate artwork dimensions to match the perspective quad
      // Use the maximum dimensions to ensure full coverage
      const topWidth = Math.hypot(
        pixelCorners[1].x - pixelCorners[0].x,
        pixelCorners[1].y - pixelCorners[0].y
      );
      const bottomWidth = Math.hypot(
        pixelCorners[2].x - pixelCorners[3].x,
        pixelCorners[2].y - pixelCorners[3].y
      );
      const leftHeight = Math.hypot(
        pixelCorners[3].x - pixelCorners[0].x,
        pixelCorners[3].y - pixelCorners[0].y
      );
      const rightHeight = Math.hypot(
        pixelCorners[2].x - pixelCorners[1].x,
        pixelCorners[2].y - pixelCorners[1].y
      );

      // Use the maximum dimensions to ensure the artwork fills the quad
      const artworkWidth = Math.max(topWidth, bottomWidth);
      const artworkHeight = Math.max(leftHeight, rightHeight);

      // Calculate perspective matrix from corners
      const matrix = this.calculatePerspectiveMatrix(pixelCorners, artworkWidth, artworkHeight);

      // Apply transform to artwork images (but not frame lighting overlay)
      const artworkImages = this.querySelectorAll('img:not(.mock-image__frame-lighting)');
      artworkImages.forEach(img => {
        img.style.width = `${artworkWidth}px`;
        img.style.height = `${artworkHeight}px`;
        img.style.transform = matrix;
        img.style.transformOrigin = '0 0';
        img.style.position = 'absolute';
        img.style.top = '0';
        img.style.left = '0';
      });

      // Handle frame lighting overlay - no transform, uses clip-path instead
      const frameLighting = this.querySelector('.mock-image__frame-lighting');
      if (frameLighting) {
        // Frame lighting stays in frame coordinates (fills container)
        frameLighting.style.width = '100%';
        frameLighting.style.height = '100%';
        frameLighting.style.position = 'absolute';
        frameLighting.style.top = '0';
        frameLighting.style.left = '0';
        frameLighting.style.objectFit = 'fill';

        // Create clip-path polygon from corners (in percentage coordinates)
        const clipPath = `polygon(${pixelCorners.map(c =>
          `${(c.x / frameWidth * 100).toFixed(3)}% ${(c.y / frameHeight * 100).toFixed(3)}%`
        ).join(', ')})`;
        frameLighting.style.clipPath = clipPath;
      }
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Calculate CSS matrix3d transform from four corner points
   * This maps a rectangle (artwork) to a quadrilateral (perspective area on frame)
   *
   * Source: Rectangle with corners [0,0], [w,0], [w,h], [0,h]
   * Dest: Quadrilateral with corners pixelCorners[0..3]
   *
   * @param {Array} corners - Array of 4 points {x, y} in pixel coordinates
   * @param {number} width - Width of source rectangle (artwork)
   * @param {number} height - Height of source rectangle (artwork)
   * @returns {string} - CSS matrix3d() transform string
   */
  calculatePerspectiveMatrix(corners, width, height) {
    // Source rectangle corners
    const src = [
      { x: 0, y: 0 },           // top-left
      { x: width, y: 0 },       // top-right
      { x: width, y: height },  // bottom-right
      { x: 0, y: height }       // bottom-left
    ];

    // Destination quadrilateral corners
    const dst = corners;

    // Calculate the perspective projection matrix (homography)
    // This uses the direct linear transform (DLT) method

    // Build the matrix equation: A * h = 0
    // Where h is the homography matrix flattened to a vector
    const A = [];

    for (let i = 0; i < 4; i++) {
      const sx = src[i].x;
      const sy = src[i].y;
      const dx = dst[i].x;
      const dy = dst[i].y;

      // Two equations per point correspondence
      A.push([
        -sx, -sy, -1, 0, 0, 0, dx * sx, dx * sy, dx
      ]);
      A.push([
        0, 0, 0, -sx, -sy, -1, dy * sx, dy * sy, dy
      ]);
    }

    // Solve using SVD (simplified for this case)
    // For 4-point correspondence, we can use a direct algebraic solution
    const h = this.solveHomography(A);

    // Convert homography matrix to CSS matrix3d
    // Homography is a 3x3 matrix:
    // [ h0  h1  h2 ]
    // [ h3  h4  h5 ]
    // [ h6  h7  h8 ]
    //
    // CSS matrix3d is 4x4:
    // [ m0  m1  m2  m3  ]
    // [ m4  m5  m6  m7  ]
    // [ m8  m9  m10 m11 ]
    // [ m12 m13 m14 m15 ]
    //
    // For 2D perspective, we embed the 3x3 in 4x4:
    // [ h0  h1  0  h2 ]
    // [ h3  h4  0  h5 ]
    // [ 0   0   1  0  ]
    // [ h6  h7  0  h8 ]

    const m = [
      h[0], h[3], 0, h[6],
      h[1], h[4], 0, h[7],
      0, 0, 1, 0,
      h[2], h[5], 0, h[8]
    ];

    return `matrix3d(${m.join(', ')})`;
  }

  /**
   * Solve homography from 8x9 matrix using pseudo-inverse
   * Simplified version assuming well-conditioned matrix
   */
  solveHomography(A) {
    // For a 4-point correspondence, we have 8 equations and 9 unknowns
    // We set h8 = 1 and solve for the other 8 values

    // This is a simplified direct solution
    // In practice, this uses numerical methods, but for typical cases:

    // Extract the linear system components
    const At = this.transposeMatrix(A, 8, 9);
    const AtA = this.multiplyMatrices(At, 9, 8, A, 8, 9);

    // Use the last column as the solution approximation
    // This is a simplification - full SVD would be more accurate

    // For now, use a simplified closed-form solution for the common case
    // where the perspective is relatively mild

    // Alternative: Use the adjoint method for small matrices
    return this.solveDirect(A);
  }

  /**
   * Direct solution for homography (simplified)
   * Uses the fact that for 4-point correspondence, we can solve directly
   */
  solveDirect(A) {
    // Build reduced 8x8 system by setting h8 = 1
    // Then solve A_reduced * h_reduced = b

    const b = A.map(row => -row[8]);
    const A_reduced = A.map(row => row.slice(0, 8));

    // Gaussian elimination (simplified - assumes well-conditioned)
    const h_reduced = this.gaussianElimination(A_reduced, b);

    // Append h8 = 1
    return [...h_reduced, 1];
  }

  /**
   * Gaussian elimination for solving linear system
   */
  gaussianElimination(A, b) {
    const n = A.length;
    const m = A[0].length;

    // Create augmented matrix
    const aug = A.map((row, i) => [...row, b[i]]);

    // Forward elimination
    for (let i = 0; i < Math.min(n, m); i++) {
      // Find pivot
      let maxRow = i;
      for (let k = i + 1; k < n; k++) {
        if (Math.abs(aug[k][i]) > Math.abs(aug[maxRow][i])) {
          maxRow = k;
        }
      }

      // Swap rows
      [aug[i], aug[maxRow]] = [aug[maxRow], aug[i]];

      // Make all rows below this one 0 in current column
      for (let k = i + 1; k < n; k++) {
        const factor = aug[k][i] / aug[i][i];
        for (let j = i; j <= m; j++) {
          aug[k][j] -= factor * aug[i][j];
        }
      }
    }

    // Back substitution
    const x = new Array(m).fill(0);
    for (let i = Math.min(n, m) - 1; i >= 0; i--) {
      x[i] = aug[i][m];
      for (let j = i + 1; j < m; j++) {
        x[i] -= aug[i][j] * x[j];
      }
      x[i] /= aug[i][i];
    }

    return x;
  }

  /**
   * Matrix transpose
   */
  transposeMatrix(A, rows, cols) {
    const At = [];
    for (let j = 0; j < cols; j++) {
      At[j] = [];
      for (let i = 0; i < rows; i++) {
        At[j][i] = A[i][j];
      }
    }
    return At;
  }

  /**
   * Matrix multiplication: C = A * B
   */
  multiplyMatrices(A, rowsA, colsA, B, rowsB, colsB) {
    if (colsA !== rowsB) {
      throw new Error('Matrix dimensions incompatible for multiplication');
    }

    const C = [];
    for (let i = 0; i < rowsA; i++) {
      C[i] = [];
      for (let j = 0; j < colsB; j++) {
        let sum = 0;
        for (let k = 0; k < colsA; k++) {
          sum += A[i][k] * B[k][j];
        }
        C[i][j] = sum;
      }
    }
    return C;
  }
}

// Register the custom element
if (!customElements.get('mock-image-css3d')) {
  customElements.define('mock-image-css3d', MockImageCss3d);
}
